import React from "react";
import { object, string, ValidationError } from "yup";
import {
  countryOptions,
  User,
  BillingFormValues,
  InputVariantTypes,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import { TextField } from "../TextField";
import { Select } from "../Select";
import { Button } from "../Button";
import { FormLayout, RowItems, RowItem } from "../FormLayout/FormLayout.styles";
import { InlineBanner } from "../InlineBanner";
import { useForm } from "../utils/";
import isEqual from "lodash/isEqual";

export type BillingDetailsUpdatesArgs = {
  userId: string;
  fieldsToUpdate: BillingFormValues;
};

export type SubmitBillingDetailsFn = (
  details: BillingDetailsUpdatesArgs
) => Promise<any>;

type Props = {
  errorMessage?: string | null;
  user: User;
  onSubmitBillingDetails: SubmitBillingDetailsFn;
  actionType?: "primary" | "secondary";
  onSuccessfulSubmit?: () => void;
  inputVariant?: InputVariantTypes;
  buttonPlacement?: "left" | "right";
  showCancel?: boolean;
  onCancel?: () => void;
};

const ButtonContainer = styled.div<{
  buttonPlacement: "left" | "right" | "spread";
}>`
  display: flex;
  justify-content: ${({ buttonPlacement }) =>
    buttonPlacement === "right"
      ? "flex-end"
      : buttonPlacement === "left"
      ? "flex-start"
      : "space-between"};
  margin-top: 32px;
  margin-bottom: 20px;
`;

const schema = (user: User) =>
  object()
    .shape({
      billingCompanyName: user.isBusinessType
        ? string().default("").min(1, "Please enter a Company Name.").required()
        : string(),
      billingEmail: string()
        .default("")
        .email("Please enter a valid email address."),
      billingAddress1: string().default(""),
      billingAddress2: string(),
      billingCountry: string(),
      billingCity: string().default(""),
      billingPostcode: string().default(""),
    })
    .test("address-validation", "", function (values) {
      const { billingAddress1, billingCity, billingPostcode } = values;

      const hasAnyAddressText = Boolean(
        billingAddress1?.trim() ||
          billingCity?.trim() ||
          billingPostcode?.trim()
      );

      // Allow other fields to be updated if no address text fields have been filled in
      if (!hasAnyAddressText) return true;

      const errors: ValidationError[] = [];

      if (!billingAddress1) {
        errors.push(
          this.createError({
            path: "billingAddress1",
            message: "Please enter the first line of your address.",
          })
        );
      }
      if (!billingCity) {
        errors.push(
          this.createError({
            path: "billingCity",
            message: "Please enter your city.",
          })
        );
      }
      if (!billingPostcode) {
        errors.push(
          this.createError({
            path: "billingPostcode",
            message: "Please enter your postcode.",
          })
        );
      }

      if (errors.length > 0) {
        throw new ValidationError(errors);
      }

      return true;
    });

export const BillingDetailsForm: React.FC<Props> = ({
  errorMessage,
  user,
  onSubmitBillingDetails,
  actionType = "secondary",
  inputVariant = InputVariantTypes.DEFAULT,
  onSuccessfulSubmit,
  buttonPlacement = "left",
  showCancel = false,
  onCancel,
}) => {
  const userValues = {
    billingCompanyName: user.billingCompanyName,
    billingEmail: user.billingEmail,
    billingAddress1: user.billingAddress1,
    billingAddress2: user.billingAddress2,
    billingCity: user.billingCity,
    billingPostcode: user.billingPostcode,
    billingCountry: user.billingLocation,
  };

  const form = useForm<BillingFormValues>({
    validationSchema: schema(user),
    initialValues: userValues,
    onSubmit: async (formValues) => {
      await onSubmitBillingDetails({
        userId: user.userId,
        fieldsToUpdate: formValues,
      });
      if (onSuccessfulSubmit) {
        onSuccessfulSubmit();
      }
    },
  });

  const {
    isSubmitting,
    handleSubmit,
    isValid,
    values,
    errors: {
      billingCompanyName,
      billingAddress1,
      billingCity,
      billingPostcode,
      billingEmail,
    },
  } = form;

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <FormLayout>
          {user.isBusinessType && (
            <RowItem>
              <TextField
                {...form.getFieldProps("billingCompanyName", "Company name")}
                isDisabled={isSubmitting}
                maxLength={100}
                errorText={billingCompanyName?.toString()}
                variant={inputVariant}
              />
            </RowItem>
          )}
          <RowItem>
            <TextField
              {...form.getFieldProps("billingAddress1", "Address line 1")}
              isDisabled={isSubmitting}
              maxLength={100}
              errorText={billingAddress1?.toString()}
              variant={inputVariant}
            />
          </RowItem>
          <RowItem>
            <TextField
              {...form.getFieldProps(
                "billingAddress2",
                "Address line 2 (optional)"
              )}
              isDisabled={isSubmitting}
              maxLength={100}
              variant={inputVariant}
            />
          </RowItem>
          <RowItems>
            <RowItem>
              <TextField
                {...form.getFieldProps("billingCity", "City")}
                isDisabled={isSubmitting}
                maxLength={100}
                errorText={billingCity?.toString()}
                variant={inputVariant}
              />
            </RowItem>
            <RowItem>
              <TextField
                {...form.getFieldProps("billingPostcode", "Postcode")}
                isDisabled={isSubmitting}
                maxLength={100}
                errorText={billingPostcode?.toString()}
                variant={inputVariant}
              />
            </RowItem>
          </RowItems>
          <RowItem>
            <Select
              {...form.getFieldProps("billingCountry", "Country")}
              items={countryOptions}
              isDisabled={isSubmitting}
              variant={inputVariant}
            />
          </RowItem>
          {user.isBusinessType && (
            <RowItem>
              <TextField
                {...form.getFieldProps("billingEmail", "Billing email address")}
                type="email"
                isDisabled={isSubmitting}
                maxLength={100}
                helpText="Your invoices will be sent to this address "
                placeholder="accounts@example.com"
                errorText={billingEmail?.toString()}
                variant={inputVariant}
              />
            </RowItem>
          )}
        </FormLayout>
        {errorMessage && (
          <div style={{ margin: "16px 0px" }}>
            <InlineBanner title="Ooops!" status="critical" onDismiss={() => {}}>
              <p>{errorMessage}</p>
            </InlineBanner>
          </div>
        )}
        <ButtonContainer
          buttonPlacement={showCancel ? "spread" : buttonPlacement}
        >
          {showCancel && (
            <Button
              textOnlyWithBorder
              disabled={isSubmitting}
              text="Cancel"
              onClick={onCancel}
            />
          )}
          <Button
            loading={isSubmitting}
            disabled={!isValid || isSubmitting || isEqual(userValues, values)}
            submit
            darkGreen={actionType === "secondary"}
            primary={actionType === "primary"}
            text="Save"
            data-testid="save-billing-details-button"
          />
        </ButtonContainer>
      </form>
    </div>
  );
};
